'use client'

import { useCallback } from 'react'
import { useRouter } from 'next/navigation'

import { Button } from '@graftcode/design-system'

import { Header } from '@common/components/Header'
import { AUTH_LOGOUT_PATH, DEFAULT_PROFILE_ITEMS } from '@common/const'
import { AppRoute } from '@common/enums'
import { useAuthUser } from '@common/hooks/useAuthUser'
import { navigateToMenuUrl } from '@common/utils/navigateToMenuUrl'

import { ACADEMY_HEADER_WRAPPER_TEST_IDS } from '@/shared/docs-test-ids.const'

import { AcademyNavbarDesktop } from '../AcademyHeaderNav/AcademyNavbarDesktop'
import { NavbarBreadcrumbs } from '../AcademyHeaderNav/NavbarBreadcrumbs'
import { NavbarSearchBar } from '../AcademyHeaderNav/NavbarSearchBar'

import styles from './AcademyHeaderWrapper.module.scss'

type AcademyHeaderWrapperProps = {
  breadcrumbs?: string[]
  onMobileMenuToggle?: () => void
  isMobile?: boolean
  isQuickStart?: boolean
  currentUnitIndex?: number
  totalUnits?: number
  onSaveAndExit?: () => void
  documentationSearchEnabled?: boolean
  onDocumentationSearchOpen?: (opts?: { query?: string }) => void
}

export function AcademyHeaderWrapper({
  breadcrumbs = [],
  onMobileMenuToggle,
  isMobile = false,
  isQuickStart,
  currentUnitIndex,
  totalUnits,
  onSaveAndExit,
  documentationSearchEnabled = false,
  onDocumentationSearchOpen,
}: AcademyHeaderWrapperProps) {
  const router = useRouter()
  const { user, isLoading } = useAuthUser()
  const isLoggedIn = !!user
  const showSignIn = !isLoading && !isLoggedIn

  const portalUrl = process.env.NEXT_PUBLIC_WEB_PORTAL_URL || 'https://portal.graftcode.com'

  const handleProfileClick = useCallback(
    (itemId: string) => {
      if (itemId === AppRoute.LOGOUT) {
        router.push(AUTH_LOGOUT_PATH)
      }
      if (itemId === AppRoute.SETTINGS) {
        navigateToMenuUrl(`${portalUrl}/${AppRoute.SETTINGS}`, router)
      }
    },
    [portalUrl, router]
  )

  if (isQuickStart) {
    return (
      <AcademyNavbarDesktop
        data-testid={ACADEMY_HEADER_WRAPPER_TEST_IDS.root}
        isQuickStart
        currentUnitIndex={currentUnitIndex}
        totalUnits={totalUnits}
        onSaveAndExit={onSaveAndExit}
      />
    )
  }

  const handleSignIn = () => {
    router.push('/auth/login')
  }

  return (
    <Header
      profileItems={DEFAULT_PROFILE_ITEMS}
      leftSlot={
        <div className={styles.breadcrumbs}>
          <NavbarBreadcrumbs segments={breadcrumbs} />
        </div>
      }
      hideActions={!isLoggedIn}
      rightSlot={
        showSignIn && !isMobile ? (
          <Button variant="primary" size="s" text="Sign In" onClick={handleSignIn} />
        ) : undefined
      }
      searchSlot={documentationSearchEnabled ? <NavbarSearchBar onOpen={onDocumentationSearchOpen} /> : undefined}
      searchDisabled={!documentationSearchEnabled}
      feedbackDisabled
      notificationsDisabled
      onProfileClick={handleProfileClick}
      onMenuToggle={onMobileMenuToggle}
    />
  )
}
